import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import useConfiguratorStore from '../store/configuratorStore';

const Container = styled.div`
  margin-bottom: 2rem;
`;

const TabsContainer = styled.div`
  display: flex;
  overflow-x: auto;
  border-bottom: 1px solid var(--gray-200);
  gap: 1rem;
  padding-bottom: 0.5rem;
  
  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 768px) {
    padding-bottom: 1rem;
  }
`;

const TabItem = styled(motion.button)`
  color: ${props => props.active ? 'var(--primary-color)' : 'var(--gray-600)'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-size: var(--text-md);
  padding: 0.75rem 1.25rem;
  border-radius: var(--rounded-md);
  background-color: ${props => props.active ? 'var(--primary-light)' : 'transparent'};
  white-space: nowrap;
  transition: var(--transition-fast);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 100%;
    height: 3px;
    background-color: var(--primary-color);
    opacity: ${props => props.active ? 1 : 0};
    transition: var(--transition-fast);
  }
  
  &:hover {
    color: ${props => props.active ? 'var(--primary-color)' : 'var(--gray-800)'};
  }
`;

const AllTabItem = styled(TabItem)`
  color: ${props => !props.hasSelection ? 'var(--primary-color)' : 'var(--gray-600)'};
  font-weight: ${props => !props.hasSelection ? '600' : '500'};
  
  &:after {
    opacity: ${props => !props.hasSelection ? 1 : 0};
  }
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: { 
      staggerChildren: 0.05
    }
  }
};

const tabVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 }
};

function SubcategoryNavigation() {
  const { 
    selectedMainCategory, 
    selectedSubcategory, 
    subcategories,
    setSubcategory
  } = useConfiguratorStore();
  
  if (!selectedMainCategory || !subcategories[selectedMainCategory]) {
    return null;
  }
  
  const currentSubcategories = subcategories[selectedMainCategory];
  
  // Handle "View All" click
  const handleAllClick = () => {
    setSubcategory(null);
  };
  
  // Handle subcategory click
  const handleSubcategoryClick = (subcategoryId) => {
    setSubcategory(subcategoryId);
  };
  
  return (
    <Container>
      <TabsContainer
        as={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <AllTabItem
          variants={tabVariants}
          active={!selectedSubcategory}
          hasSelection={selectedSubcategory}
          onClick={handleAllClick}
          whileTap={{ scale: 0.97 }}
        >
          View All
        </AllTabItem>
        
        {currentSubcategories.map(subcategory => (
          <TabItem
            key={subcategory.id}
            variants={tabVariants}
            active={selectedSubcategory === subcategory.id}
            onClick={() => handleSubcategoryClick(subcategory.id)}
            whileTap={{ scale: 0.97 }}
          >
            {subcategory.name}
          </TabItem>
        ))}
      </TabsContainer>
    </Container>
  );
}

export default SubcategoryNavigation;